import React, { useState } from "react";
import { useLanguage } from "../App";
import { X } from "lucide-react";
import axios from "axios";

const API = `/api`;

export default function ChangePasswordModal({ open, onClose }) {
  const { t } = useLanguage();
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPwd !== confirmPwd) {
      setError(t("auth_passwords_no_match"));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPwd,
        new_password: newPwd,
      });
      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
        setCurrentPwd("");
        setNewPwd("");
        setConfirmPwd("");
      }, 1500);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    onClose();
    setError("");
    setSuccess(false);
    setCurrentPwd("");
    setNewPwd("");
    setConfirmPwd("");
  };

  return (
    <div
      data-testid="change-password-overlay"
      onClick={close}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(8, 4, 2, 0.85)",
        backdropFilter: "blur(12px)",
      }}
    >
      <div
        data-testid="change-password-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: "400px",
          margin: "0 1rem",
          background: "#1A1008",
          border: "1px solid rgba(201,168,76,0.2)",
          borderRadius: "2px",
          padding: "2.5rem 2rem",
          position: "relative",
        }}
      >
        <button
          onClick={close}
          style={{
            position: "absolute",
            top: "1rem",
            right: "1rem",
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "rgba(184,149,106,0.5)",
            padding: "0.25rem",
          }}
        >
          <X size={18} />
        </button>

        <h2
          className="font-logo"
          style={{
            fontSize: "1.1rem",
            letterSpacing: "0.15em",
            color: "#D4B878",
            textTransform: "uppercase",
            marginBottom: "1.5rem",
            textAlign: "center",
          }}
        >
          {t("auth_change_password")}
        </h2>

        {success ? (
          <p data-testid="change-password-success" style={{ textAlign: "center", color: "#8DB87A", fontFamily: "'DM Sans',sans-serif", fontSize: "0.9rem" }}>
            {t("auth_password_changed")}
          </p>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div>
              <label style={labelStyle}>{t("auth_current_password")}</label>
              <input
                data-testid="current-password-input"
                className="khatira-input"
                type="password"
                value={currentPwd}
                onChange={(e) => setCurrentPwd(e.target.value)}
                required
              />
            </div>
            <div>
              <label style={labelStyle}>{t("auth_new_password")}</label>
              <input
                data-testid="new-password-input"
                className="khatira-input"
                type="password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                required
                minLength={6}
              />
            </div>
            <div>
              <label style={labelStyle}>{t("auth_confirm_new_password")}</label>
              <input
                data-testid="confirm-password-input"
                className="khatira-input"
                type="password"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                required
                minLength={6}
              />
            </div>

            {error && (
              <div
                data-testid="change-password-error"
                style={{
                  padding: "0.75rem 1rem",
                  background: "rgba(180,60,40,0.08)",
                  border: "1px solid rgba(180,60,40,0.25)",
                  borderRadius: "2px",
                  color: "#D4836A",
                  fontFamily: "'DM Sans',sans-serif",
                  fontSize: "0.82rem",
                }}
              >
                {error}
              </div>
            )}

            <button
              data-testid="change-password-submit"
              className="btn-primary"
              type="submit"
              disabled={submitting}
              style={{
                padding: "0.85rem",
                fontSize: "0.85rem",
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                marginTop: "0.5rem",
                width: "100%",
              }}
            >
              {submitting ? "..." : t("auth_change_password")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const labelStyle = {
  fontFamily: "'DM Sans', sans-serif",
  fontSize: "0.75rem",
  fontWeight: 400,
  color: "#B8956A",
  letterSpacing: "0.1em",
  display: "block",
  marginBottom: "0.4rem",
  textTransform: "uppercase",
};
